import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Col, Table, Input, Label } from "reactstrap";
import axios from "axios";
import ExportJsonExcel from "js-export-excel";

function ReporteMargenesMeli() {
  const { user } = useContext(AuthContext);
  const URL_ARTICULOS = process.env.REACT_APP_URL_ARTICULOS;
  const URL_INVENTARIOS = process.env.REACT_APP_URL_INVENTARIOS;

  const [articulos, setArticulos] = useState([]);
  const [inventarios, setInventarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMarca, setFilterMarca] = useState("");
  const [marcas, setMarcas] = useState([]);

  function actualizar() {
    setLoading(true);
    const auth = { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } };
    Promise.all([
      axios.get(URL_ARTICULOS, auth),
      axios.get(`${URL_INVENTARIOS}Acumulado`, auth),
    ])
      .then(([{ data: arts }, { data: invs }]) => {
        setArticulos(arts);
        setInventarios(invs);
        const ms = [...new Set(arts.flatMap(a => a.marcas?.map(m => m.name || m) || []).filter(Boolean))].sort();
        setMarcas(ms);
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  }

  useEffect(() => { actualizar(); }, []);

  const rows = useMemo(() => {
    // Build inventory map: articuloId → { cantidad, costoUnitario }
    const invMap = {};
    inventarios.forEach((i) => {
      const id = i.articulos?.[0]?._id || i.articulos?.[0];
      if (!id) return;
      if (!invMap[id]) invMap[id] = { cantidad: 0, costoUnitario: 0 };
      invMap[id].cantidad += parseFloat(i.cantidad) || 0;
      // weighted average
      const prev = invMap[id];
      const prevTotal = prev.cantidad - (parseFloat(i.cantidad) || 0);
      invMap[id].costoUnitario =
        prevTotal > 0
          ? (prevTotal * prev.costoUnitario + (parseFloat(i.cantidad) || 0) * (parseFloat(i.costoUnitario) || 0)) / prev.cantidad
          : parseFloat(i.costoUnitario) || 0;
    });

    return articulos
      .filter((a) => a.MLM && a.MLM !== "")
      .map((a) => {
        const inv = invMap[a._id] || { cantidad: 0, costoUnitario: 0 };
        const costoMX = parseFloat(a.costoMX) || parseFloat(inv.costoUnitario) || 0;
        const precioVenta = parseFloat(a.venta) || 0;
        const comisionMeli = parseFloat(a.comision) || 0;
        const otrosCostos = parseFloat(a.otrosCostos) || 0;

        // precioNeto = precio / 1.16 * 2% retencion (referencia Huara)
        const retencion = (precioVenta / 1.16) * 0.02;
        const totalDescuentos = comisionMeli + otrosCostos + retencion;
        const precioNeto = precioVenta - totalDescuentos;
        const utilidad = precioNeto - costoMX;
        const margen = precioVenta > 0 ? (utilidad / precioVenta) * 100 : 0;

        const marcaNombre = a.marcas?.[0]?.name || a.marcas?.[0] || "";
        return {
          _id: a._id,
          mlm: a.MLM,
          asin: a.ASIN || "",
          nombre: a.nombre,
          marca: marcaNombre,
          costoMX,
          precioVenta,
          comisionMeli,
          retencion,
          otrosCostos,
          totalDescuentos,
          precioNeto,
          utilidad,
          margen,
          cantidadInv: inv.cantidad,
        };
      })
      .filter((r) => {
        const matchSearch = !search || r.nombre.toLowerCase().includes(search.toLowerCase()) || r.mlm.toLowerCase().includes(search.toLowerCase());
        const matchMarca = !filterMarca || r.marca === filterMarca;
        return matchSearch && matchMarca;
      })
      .sort((a, b) => a.margen - b.margen);
  }, [articulos, inventarios, search, filterMarca]);

  function rowColor(margen) {
    if (margen < 10) return { backgroundColor: "#fee2e2" };
    if (margen > 25) return { backgroundColor: "#dcfce7" };
    return {};
  }

  function excel() {
    const option = {
      fileName: "MargenesMeli",
      datas: [{
        sheetData: rows.map((r) => ({
          MLM: r.mlm, Artículo: r.nombre, Marca: r.marca,
          CostoMX: r.costoMX.toFixed(2), PrecioVenta: r.precioVenta.toFixed(2),
          "Comisión Meli": r.comisionMeli.toFixed(2), "Retención": r.retencion.toFixed(2),
          "Otros Costos": r.otrosCostos.toFixed(2), PrecioNeto: r.precioNeto.toFixed(2),
          Utilidad: r.utilidad.toFixed(2), "Margen %": r.margen.toFixed(1),
          Inventario: r.cantidadInv,
        })),
        sheetName: "Margenes",
        sheetHeader: ["MLM","Artículo","Marca","Costo MX","Precio Venta","Comisión Meli","Retención","Otros Costos","Precio Neto","Utilidad","Margen %","Inventario"],
      }],
    };
    new ExportJsonExcel(option).saveExcel();
  }

  const sec = { borderLeft: "4px solid #3b82f6", paddingLeft: 12, marginBottom: 8, color: "#1e3a8a", fontWeight: 600, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em" };
  const block = { background: "#f8fafc", borderRadius: 8, padding: "12px 16px", marginBottom: 14 };

  const totales = useMemo(() => ({
    utilidad: rows.reduce((s, r) => s + r.utilidad, 0),
    margenProm: rows.length ? rows.reduce((s, r) => s + r.margen, 0) / rows.length : 0,
  }), [rows]);

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_ventas ? (
        <div className="card container col-12">
          {/* Toolbar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, marginTop: 8 }}>
            <h4 style={{ margin: 0 }}>
              📊 Márgenes Meli
              <span style={{ background: "#dbeafe", color: "#1e3a8a", borderRadius: 20, padding: "3px 14px", fontWeight: 700, fontSize: "0.85rem", marginLeft: 10 }}>
                {rows.length} artículos
              </span>
            </h4>
            <div style={{ display: "flex", gap: 6 }}>
              <Button size="sm" color="success" onClick={actualizar} disabled={loading}>
                {loading ? "Cargando..." : "🔄 Actualizar"}
              </Button>
              <Button size="sm" color="primary" onClick={excel}>Excel <i className="far fa-file-excel"></i></Button>
              <Button size="sm" color="danger" href="/MenuVentas">← Regresar</Button>
            </div>
          </div>

          {/* Filtros */}
          <div style={block}>
            <div style={sec}>🔍 Filtros</div>
            <Row>
              <Col md={3}>
                <Label>Marca</Label>
                <Input type="select" value={filterMarca} onChange={(e) => setFilterMarca(e.target.value)}>
                  <option value="">Todas</option>
                  {marcas.map((m) => <option key={m} value={m}>{m}</option>)}
                </Input>
              </Col>
              <Col md={4}>
                <Label>Buscar Artículo / MLM</Label>
                <Input type="text" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </Col>
            </Row>
          </div>

          {/* Leyenda colores */}
          <div style={{ marginBottom: 8, fontSize: "0.78rem", display: "flex", gap: 14 }}>
            <span style={{ background: "#fee2e2", borderRadius: 4, padding: "2px 8px" }}>Margen &lt; 10%</span>
            <span style={{ background: "#dcfce7", borderRadius: 4, padding: "2px 8px" }}>Margen &gt; 25%</span>
          </div>

          {/* Tabla */}
          <Table size="sm" bordered striped className="table-responsive-xl" style={{ fontSize: "0.80rem" }}>
            <thead className="thead-light">
              <tr>
                <th>MLM</th>
                <th>Artículo</th>
                <th>Marca</th>
                <th>Costo MX</th>
                <th>P. Venta</th>
                <th>Comisión</th>
                <th>Retención</th>
                <th>Otros</th>
                <th>P. Neto</th>
                <th>Utilidad</th>
                <th>Margen %</th>
                <th>Inv.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} style={rowColor(r.margen)}>
                  <td>{r.mlm}</td>
                  <td>{r.nombre}</td>
                  <td>{r.marca}</td>
                  <td>${r.costoMX.toFixed(2)}</td>
                  <td>${r.precioVenta.toFixed(2)}</td>
                  <td>${r.comisionMeli.toFixed(2)}</td>
                  <td>${r.retencion.toFixed(2)}</td>
                  <td>${r.otrosCostos.toFixed(2)}</td>
                  <td><b>${r.precioNeto.toFixed(2)}</b></td>
                  <td style={{ color: r.utilidad < 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
                    ${r.utilidad.toFixed(2)}
                  </td>
                  <td style={{ color: r.margen < 10 ? "#dc2626" : r.margen > 25 ? "#16a34a" : "#000", fontWeight: 600 }}>
                    {r.margen.toFixed(1)}%
                  </td>
                  <td>{r.cantidadInv}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700, background: "#f1f5f9" }}>
                <td colSpan={9} className="text-right">PROMEDIOS / TOTAL</td>
                <td style={{ color: totales.utilidad < 0 ? "#dc2626" : "#16a34a" }}>${totales.utilidad.toFixed(2)}</td>
                <td>{totales.margenProm.toFixed(1)}%</td>
                <td></td>
              </tr>
            </tbody>
          </Table>
        </div>
      ) : undefined}
    </>
  );
}

export default ReporteMargenesMeli;
